import { useState } from 'react';
import PacActivationDialog from './PacActivationDialog';

export interface PacDetectedAccount {
  codigoComercio: string;
  banco: string;
  bankCode: string | null;
  numeroCuenta: string;
  tipoCuenta: string;
  rutTitular: string;
  isVigente: boolean;
  pacSupported: boolean;
  pacBankUrl: string | null;
  mandateStatus: 'active' | 'suspended' | 'revoked' | null;
  mandateActivatedAt: string | null;
  hasReceipt: boolean;
}

interface Props {
  account: PacDetectedAccount;
  customerCode: string | null;
  onReceiptUploaded: () => void;
}

const STATUS_LABEL: Record<NonNullable<PacDetectedAccount['mandateStatus']>, { label: string; className: string }> = {
  active: { label: 'Activo', className: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' },
  suspended: { label: 'Suspendido', className: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400' },
  revoked: { label: 'Revocado', className: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300' },
};

export default function PacAccountRow({ account, customerCode, onReceiptUploaded }: Props) {
  const [dialogOpen, setDialogOpen] = useState(false);

  const statusBadge = account.mandateStatus ? STATUS_LABEL[account.mandateStatus] : null;
  const isActiveMandate = account.mandateStatus === 'active';

  return (
    <li className="py-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-sm font-medium">{account.banco}</p>
            {!account.isVigente && <span className="text-[11px] text-amber-600">No vigente</span>}
            {statusBadge && <span className={`inline-flex items-center rounded px-2 py-0.5 text-xs font-medium ${statusBadge.className}`}>Mandato {statusBadge.label}</span>}
            {account.hasReceipt && (
              <span className="inline-flex items-center gap-1 rounded bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
                <span className="material-symbols-outlined text-sm">attach_file</span>
                Comprobante subido
              </span>
            )}
          </div>
          <p className="mt-0.5 font-mono text-xs text-muted-foreground">
            {account.tipoCuenta} · {account.numeroCuenta}
          </p>
          <p className="mt-0.5 text-[11px] text-muted-foreground">Código comercio {account.codigoComercio}</p>
        </div>

        <div className="flex flex-shrink-0 flex-col items-end">
          {account.pacSupported ? (
            <button
              type="button"
              onClick={() => setDialogOpen(true)}
              disabled={isActiveMandate}
              className="inline-flex items-center gap-1 rounded-md bg-flux-primary px-3 py-1.5 text-xs font-medium text-white shadow-sm hover:bg-flux-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <span className="material-symbols-outlined text-sm">{isActiveMandate ? 'check_circle' : 'verified'}</span>
              {isActiveMandate ? 'Mandato activo' : 'Activar mandato'}
            </button>
          ) : (
            <span className="text-[11px] text-muted-foreground">PAC no disponible para este banco</span>
          )}
        </div>
      </div>

      {account.pacSupported && (
        <PacActivationDialog open={dialogOpen} onOpenChange={setDialogOpen} account={account} customerCode={customerCode} onReceiptUploaded={onReceiptUploaded} />
      )}
    </li>
  );
}
